/**
 * Direct DOM manipulation helpers for SEO meta tags.
 * Replaces react-helmet-async which fails to inject tags in some environments.
 */

/**
 * Safely serialize JSON for injection into <script> tags.
 * Escapes </ sequences that would prematurely close the script block.
 */
function safeJsonStringify(obj: Record<string, unknown>): string | null {
  try {
    let json = JSON.stringify(obj);
    json = json.replace(/<\//g, '<\\/');
    return json;
  } catch {
    return null;
  }
}

/** Set or create a <meta name="..."> tag */
export function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute('data-seo', 'true');
}

/** Set or create a <meta property="..."> tag */
export function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute('data-seo', 'true');
}

/** Set or create a <meta httpEquiv="..."> tag */
export function setMetaHttpEquiv(httpEquiv: string, content: string) {
  let el = document.querySelector(`meta[http-equiv="${httpEquiv}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('http-equiv', httpEquiv);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute('data-seo', 'true');
}

/** Set or create a single <link rel="canonical"> */
export function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
  el.setAttribute('data-seo', 'true');
}

/** Set or create a <link rel="..."> by rel+type combo */
export function setLink(rel: string, href: string, attrs?: Record<string, string>) {
  const selector = attrs?.type
    ? `link[rel="${rel}"][type="${attrs.type}"]`
    : `link[rel="${rel}"][href="${href}"]`;
  let el = document.querySelector(selector) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  if (attrs) Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  el.setAttribute('data-seo', 'true');
}

/** Remove and re-create all hreflang tags */
export function setHreflangLinks(links: Array<{ lang: string; href: string }>) {
  // Remove ALL existing hreflang links (broad selector to prevent duplicates from any source)
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
  
  for (const { lang, href } of links) {
    const el = document.createElement('link');
    el.rel = 'alternate';
    el.hreflang = lang;
    el.href = href;
    el.setAttribute('data-seo', 'hreflang');
    document.head.appendChild(el);
  }
}

/** Remove and re-create all JSON-LD scripts managed by SEOHead */
export function setJsonLd(schemas: Record<string, unknown>[]) {
  document.querySelectorAll('script[data-seo="jsonld"]').forEach(el => el.remove());
  
  for (const schema of schemas) {
    const json = safeJsonStringify(schema);
    if (!json) continue; // Skip invalid schemas
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = json;
    script.setAttribute('data-seo', 'jsonld');
    document.head.appendChild(script);
  }
}

/** Remove and re-create OG locale alternate tags */
export function setOgLocaleAlternates(locales: string[]) {
  document.querySelectorAll('meta[property="og:locale:alternate"][data-seo="true"]').forEach(el => el.remove());
  
  for (const locale of locales) {
    const el = document.createElement('meta');
    el.setAttribute('property', 'og:locale:alternate');
    el.setAttribute('content', locale);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
}

/** Set <html> attributes */
export function setHtmlAttrs(lang: string, dir: 'ltr' | 'rtl') {
  document.documentElement.lang = lang;
  document.documentElement.dir = dir;
}

/** Remove and re-create link tags with a specific data-seo value */
export function setLinksGroup(groupId: string, links: Array<{ rel: string; href: string; attrs?: Record<string, string> }>) {
  document.querySelectorAll(`link[data-seo="${groupId}"]`).forEach(el => el.remove());
  
  for (const { rel, href, attrs } of links) {
    const el = document.createElement('link');
    el.rel = rel;
    el.href = href;
    if (attrs) Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute('data-seo', groupId);
    document.head.appendChild(el);
  }
}
